<!--
  Four branches (input/textarea × masked/unmasked) gated by *ngIf because
  NgxMaskDirective's selector matches on `[mask]` attribute presence and
  its writeValue is async — keeping it off the unmasked path avoids a CD
  race. a11y additions must be applied to all four branches.
-->

<ng-container *ngIf="!multiline; else textareaTpl">
  <input
    *ngIf="!mask && !imask; else maskedOrIMaskInputTpl"
    #inner
    class="app-input__control"
    [attr.id]="id"
    [formControl]="_innerControl"
    [attr.type]="type"
    [attr.name]="name"
    [attr.placeholder]="placeholder || null"
    [attr.autocomplete]="autocomplete"
    [attr.autofocus]="autofocus || null"
    [attr.inputmode]="inputmode ?? (type === 'number' ? 'numeric' : null)"
    [attr.min]="min"
    [attr.max]="max"
    [attr.step]="step"
    [attr.minlength]="minlength"
    [attr.maxlength]="maxlength"
    [attr.pattern]="pattern"
    [attr.readonly]="readonly || null"
    [attr.required]="required || null"
    [attr.aria-label]="ariaLabel"
    [attr.aria-labelledby]="ariaLabelledby"
    [attr.aria-describedby]="_innerAriaDescribedBy"
    [attr.aria-invalid]="errorState"
    [attr.aria-required]="required"
    [attr.role]="role"
    [attr.aria-autocomplete]="ariaAutocomplete"
    [attr.aria-expanded]="ariaExpanded"
    [attr.aria-controls]="ariaControls"
    [attr.aria-activedescendant]="ariaActivedescendant"
    (input)="_handleInput($event)"
  />
  <ng-template #maskedOrIMaskInputTpl>
    <input
      *ngIf="!imask; else imaskInputTpl"
      #inner
      class="app-input__control"
      [attr.id]="id"
      [formControl]="_innerControl"
      [attr.type]="type"
      [attr.name]="name"
      [attr.placeholder]="placeholder || null"
      [attr.autocomplete]="autocomplete"
      [attr.autofocus]="autofocus || null"
      [attr.inputmode]="inputmode ?? (type === 'number' ? 'numeric' : null)"
      [attr.min]="min"
      [attr.max]="max"
      [attr.step]="step"
      [attr.minlength]="minlength"
      [attr.maxlength]="maxlength"
      [attr.pattern]="pattern"
      [attr.readonly]="readonly || null"
      [attr.required]="required || null"
      [attr.aria-label]="ariaLabel"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-describedby]="_innerAriaDescribedBy"
      [attr.aria-invalid]="errorState"
      [attr.aria-required]="required"
      [attr.role]="role"
      [attr.aria-autocomplete]="ariaAutocomplete"
      [attr.aria-expanded]="ariaExpanded"
      [attr.aria-controls]="ariaControls"
      [attr.aria-activedescendant]="ariaActivedescendant"
      [mask]="mask"
      [thousandSeparator]="thousandSeparator"
      [decimalMarker]="decimalMarker"
      [prefix]="prefix"
      [suffix]="suffix"
      [dropSpecialCharacters]="dropSpecialCharacters"
      (input)="_handleInput($event)"
    />
    <ng-template #imaskInputTpl>
      <input
        #inner
        class="app-input__control"
        [attr.id]="id"
        [formControl]="_innerControl"
        [attr.type]="type"
        [attr.name]="name"
        [attr.placeholder]="placeholder || null"
        [attr.autocomplete]="autocomplete"
        [attr.autofocus]="autofocus || null"
        [attr.inputmode]="inputmode ?? (type === 'number' ? 'numeric' : null)"
        [attr.min]="min"
        [attr.max]="max"
        [attr.step]="step"
        [attr.minlength]="minlength"
        [attr.maxlength]="maxlength"
        [attr.pattern]="pattern"
        [attr.readonly]="readonly || null"
        [attr.required]="required || null"
        [attr.aria-label]="ariaLabel"
        [attr.aria-labelledby]="ariaLabelledby"
        [attr.aria-describedby]="_innerAriaDescribedBy"
        [attr.aria-invalid]="errorState"
        [attr.aria-required]="required"
        [attr.role]="role"
        [attr.aria-autocomplete]="ariaAutocomplete"
        [attr.aria-expanded]="ariaExpanded"
        [attr.aria-controls]="ariaControls"
        [attr.aria-activedescendant]="ariaActivedescendant"
        [imask]="imask"
        [unmask]="unmask"
        (input)="_handleInput($event)"
      />
    </ng-template>
  </ng-template>
</ng-container>

<ng-template #textareaTpl>
  <textarea
    *ngIf="!mask && !imask; else maskedOrIMaskTextareaTpl"
    #inner
    class="app-input__control app-input__control--textarea"
    [class.app-input__control--autosize]="autosize"
    [class.app-input__control--resize-none]="resize === 'none'"
    [class.app-input__control--resize-horizontal]="resize === 'horizontal'"
    [class.app-input__control--resize-both]="resize === 'both'"
    [attr.id]="id"
    [formControl]="_innerControl"
    [attr.name]="name"
    [attr.placeholder]="placeholder || null"
    [attr.autocomplete]="autocomplete"
    [attr.autofocus]="autofocus || null"
    [attr.inputmode]="inputmode ?? (type === 'number' ? 'numeric' : null)"
    [attr.minlength]="minlength"
    [attr.maxlength]="maxlength"
    [attr.readonly]="readonly || null"
    [attr.required]="required || null"
    [attr.rows]="autosize ? null : rows"
    [attr.aria-label]="ariaLabel"
    [attr.aria-labelledby]="ariaLabelledby"
    [attr.aria-describedby]="_innerAriaDescribedBy"
    [attr.aria-invalid]="errorState"
    [attr.aria-required]="required"
    [cdkTextareaAutosize]="autosize"
    [cdkAutosizeMinRows]="minRows"
    [cdkAutosizeMaxRows]="maxRows"
    (input)="_handleInput($event)"
  ></textarea>
  <ng-template #maskedOrIMaskTextareaTpl>
    <textarea
      *ngIf="!imask; else imaskTextareaTpl"
      #inner
      class="app-input__control app-input__control--textarea"
      [class.app-input__control--autosize]="autosize"
      [attr.id]="id"
      [formControl]="_innerControl"
      [attr.name]="name"
      [attr.placeholder]="placeholder || null"
      [attr.autocomplete]="autocomplete"
      [attr.autofocus]="autofocus || null"
      [attr.inputmode]="inputmode ?? (type === 'number' ? 'numeric' : null)"
      [attr.minlength]="minlength"
      [attr.maxlength]="maxlength"
      [attr.readonly]="readonly || null"
      [attr.required]="required || null"
      [attr.rows]="autosize ? null : rows"
      [attr.aria-label]="ariaLabel"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-describedby]="_innerAriaDescribedBy"
      [attr.aria-invalid]="errorState"
      [attr.aria-required]="required"
      [cdkTextareaAutosize]="autosize"
      [cdkAutosizeMinRows]="minRows"
      [cdkAutosizeMaxRows]="maxRows"
      [mask]="mask"
      [thousandSeparator]="thousandSeparator"
      [decimalMarker]="decimalMarker"
      [prefix]="prefix"
      [suffix]="suffix"
      [dropSpecialCharacters]="dropSpecialCharacters"
      (input)="_handleInput($event)"
    ></textarea>
    <ng-template #imaskTextareaTpl>
      <textarea
        #inner
        class="app-input__control app-input__control--textarea"
        [class.app-input__control--autosize]="autosize"
        [attr.id]="id"
        [formControl]="_innerControl"
        [attr.name]="name"
        [attr.placeholder]="placeholder || null"
        [attr.autocomplete]="autocomplete"
        [attr.autofocus]="autofocus || null"
        [attr.inputmode]="inputmode ?? (type === 'number' ? 'numeric' : null)"
        [attr.minlength]="minlength"
        [attr.maxlength]="maxlength"
        [attr.readonly]="readonly || null"
        [attr.required]="required || null"
        [attr.rows]="autosize ? null : rows"
        [attr.aria-label]="ariaLabel"
        [attr.aria-labelledby]="ariaLabelledby"
        [attr.aria-describedby]="_innerAriaDescribedBy"
        [attr.aria-invalid]="errorState"
        [attr.aria-required]="required"
        [cdkTextareaAutosize]="autosize"
        [cdkAutosizeMinRows]="minRows"
        [cdkAutosizeMaxRows]="maxRows"
        [imask]="imask"
        [unmask]="unmask"
        (input)="_handleInput($event)"
      ></textarea>
    </ng-template>
  </ng-template>
</ng-template>
